import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [pendingApproval, setPendingApproval] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Always read token from localStorage to get the latest value
    const currentToken = localStorage.getItem('access_token');
    
    if (!currentToken) {
      setUser(null);
      setOrganization(null);
      setToken(null);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(response.data);
      setToken(currentToken);
      // Set organization from response
      if (response.data.organization) {
        setOrganization(response.data.organization);
        localStorage.setItem('organization', JSON.stringify(response.data.organization));
      }
      setPendingApproval(false);
    } catch (error) {
      // Not authenticated
      setUser(null);
      setOrganization(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('organization');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/api/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      const { access_token, user: userData, organization: orgData } = response.data;
      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Store organization info and trigger theme refresh
      if (orgData) {
        setOrganization(orgData);
        localStorage.setItem('organization', JSON.stringify(orgData));
        // Dispatch event to trigger theme refresh
        window.dispatchEvent(new CustomEvent('organizationChanged', { detail: orgData }));
      }
      
      setPendingApproval(false);
      return { user: userData, organization: orgData };
    } catch (error) {
      if (error.response?.status === 403) {
        setPendingApproval(true);
      }
      throw error;
    }
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    
    // Check if pending approval
    if (response.data.status === 'pending_approval') {
      setPendingApproval(true);
      return { pending: true, ...response.data };
    }
    
    const { access_token, user: userData, organization: orgData } = response.data;
    localStorage.setItem('access_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Store organization info
    if (orgData) {
      setOrganization(orgData);
      localStorage.setItem('organization', JSON.stringify(orgData));
    }
    
    return { user: userData, organization: orgData };
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API}/api/auth/google/callback`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    
    // Check if pending approval
    if (response.data.status === 'pending_approval') {
      setPendingApproval(true);
      return { pending: true, ...response.data };
    }
    
    const { access_token, user: userData, organization: orgData } = response.data;
    localStorage.setItem('access_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Store organization info and trigger theme refresh
    if (orgData) {
      setOrganization(orgData);
      localStorage.setItem('organization', JSON.stringify(orgData));
      // Dispatch event to trigger theme refresh
      window.dispatchEvent(new CustomEvent('organizationChanged', { detail: orgData }));
    }
    
    return { user: userData, organization: orgData };
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('organization');
    setToken(null);
    setUser(null);
    setOrganization(null);
    setPendingApproval(false);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Admin functions
  const getPendingUsers = async () => {
    const response = await axios.get(`${API}/api/auth/users/pending`, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  };

  const approveUser = async (userId) => {
    await axios.post(`${API}/api/auth/users/${userId}/approve`, {}, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  };

  const rejectUser = async (userId) => {
    await axios.post(`${API}/api/auth/users/${userId}/reject`, {}, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  };

  const getAllUsers = async () => {
    const response = await axios.get(`${API}/api/auth/users`, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return response.data;
  };

  const value = {
    user,
    organization,
    token,
    loading,
    pendingApproval,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    isAdmin,
    checkAuth,
    getPendingUsers,
    approveUser,
    rejectUser,
    getAllUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
